import {
  PaginationSort,
  GetAllClaimantsQuery,
  GetAllClaimantsQueryVariables,
} from '../gql/graphql';
import { getPaginatedNodes, toPagination } from './utils';
import { useEffect, useMemo } from 'react';
import { graphql } from '../gql';
import { useQuery } from '@apollo/client';

export interface ClaimantOption {
  label: string;
  value: string;
}

export const GET_ALL_CLAIMANTS = graphql(`
  query GetAllClaimants(
    $claimantFilterInput: ClaimantFilterInput
    $pagination: PaginationRequest
    $sort: PaginationSort
  ) {
    claimants(
      filters: $claimantFilterInput
      pagination: $pagination
      sort: $sort
    ) {
      edges {
        cursor
        node {
          uuid
          firstName
          lastName
          nameSearch
        }
      }
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
    }
  }
`);

export function useGetAllClaimantsQuery({
  pageNumber = 0,
  pageSize = 50,
  enabled = true,
  sort,
  nameSearch,
}: {
  pageNumber?: number;
  pageSize?: number;
  enabled?: boolean;
  sort?: PaginationSort;
  nameSearch?: string;
}) {
  const variables = {
    claimantFilterInput: nameSearch ? { nameSearch } : {},
    pagination: toPagination(pageNumber, pageSize),
    sort: sort || { key: 'NAME_SEARCH', order: 'ASC' },
  };

  const { loading, error, data, refetch, fetchMore } = useQuery<
    GetAllClaimantsQuery,
    GetAllClaimantsQueryVariables
  >(GET_ALL_CLAIMANTS, {
    variables,
    skip: !enabled,
    fetchPolicy: 'network-only',
  });

  const claimants = useMemo(() => {
    if (!data || !data.claimants || !data.claimants.edges) return [];

    const claimantOptions: ClaimantOption[] = [];
    getPaginatedNodes(data.claimants).forEach((node) => {
      if (node.uuid) {
        const label =
          node.nameSearch ||
          `${node.firstName || ''} ${node.lastName || ''}`.trim();
        claimantOptions.push({
          value: node.uuid,
          label: label || 'Unknown',
        });
      }
    });

    return claimantOptions;
  }, [data]);

  const pageInfo = data?.claimants?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;

  useEffect(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled, refetch]);

  return {
    error,
    loading,
    data,
    claimants,
    refetch,
    fetchMore,
    hasNextPage,
    pageInfo,
  };
}
